import {
  useState,
  type FC,
  useEffect,
  type ComponentProps,
  type PropsWithChildren,
  type ReactElement,
} from 'react';
import { date } from '@seek/ca-myprofile-utils';
import {
  Box,
  ButtonIcon,
  CheckboxStandalone,
  Hidden,
  IconCompose,
  IconHistory,
  IconMail,
  IconStar,
  Inline,
  Stack,
  Text,
  IconEducation,
  IconMoney,
  Notice,
  TooltipRenderer,
  Columns,
  Column,
  IconLocation,
} from 'braid-design-system';
import { vars } from 'braid-design-system/css';
import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import buildHandleEnterAndSpaceKeyPress from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import { formatSalaryValues } from 'src/libs/formatSalaryValues';
import {
  trackCandidateCardStatus,
  trackClickedEmailsInCandidateList,
  trackClickedNotesInCandidateList,
  trackClickedPreviousApplicationsInCandidateList,
} from 'src/utils/events';
import type { SalaryType } from 'src/App/AppPageBlock/graphql/cm-types.generated';
import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';
import { NewBadge } from 'src/components/common/NewBadge';
import { BillableBadge } from 'src/components/common/BillableBadge';
import { AppliedDate } from './AppliedDate';
import * as styles from './JobApplicationCard.css';
import translations from './.vocab';
import { MatchedQualities } from './MatchedQualities';
import { AttachmentButtons } from './AttachmentButtons';
import { RoleRequirementsDisclosure } from './RoleRequirementsDisclosure';
import {
  MoveStatusMenu,
  NotSuitableAndShortlistButtons,
} from './StatusActions';
import { CertsyIdentityVerified } from './CertsyIdentityVerified';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import { VerifiedCredentialsBadge } from 'src/components/common/VerifiedCredentialsBadge';

export interface JobApplicationCardProps {
  isMobile: boolean;
  userId: number | null;
  seekAdvertiserId: string;
  jobId: string;
  currentBucket: BUCKETS;
  shouldShowNewBadge: boolean;
  shouldShowBillableBadge: boolean;
  shouldShowVerifiedCredentialsBadge: boolean;
  jobApplication: {
    id: string;
    candidateId: string;
    fullName: string;
    isUnread: boolean;
    appliedDate: string;
    source: number;
    rating: number;
  };
  lastRole?: {
    from: {
      month: number;
      year: number;
    };
    to?: {
      month: number;
      year: number;
    };
    title: string;
    company: string;
  };
  roleRequirements: ComponentProps<
    typeof RoleRequirementsDisclosure
  >['roleRequirements'];
  applicationListState: 'collapsed' | 'expanded';
  matchedQualities: {
    qualities: Array<{ id: string; displayLabel: string; isTop: boolean }>;
    hasError: boolean;
  };
  salary?: {
    salaryType: SalaryType;
    minimumSalaryValue: number;
    maximumSalaryValue: number | null;
    currency: string;
  };
  certsyVerifiedFullName?: string;
  education?: {
    name: string;
    institute: string;
    completionYear?: number;
  };
  homeLocation?: {
    description: string;
  };
  attachments: Array<{ id: number | string; type: number }>;
  emailCount: number;
  previousApplicationCount: number;
  noteCount: number;
  checkboxProps: {
    checked: boolean;
    onChange: () => void;
  };
  locale: Locale;
  openDetailsDrawer: (
    tab: JOB_APPLICATION_TAB,
    applicationCardIsExpanded?: boolean,
  ) => void;
  openDetailsDrawerProfileTab: (applicationCardIsExpanded?: boolean) => void;
  markApplicationAsRead: () => void;
  changeStatus: (bucket: BUCKETS, isExpanded?: boolean) => Promise<void>;
  onClickRating: (index: number) => number;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
}

type RoleRequirementsDisclosureState =
  | {
      hasRoleRequirements: true;
      isExpanded: boolean;
    }
  | {
      hasRoleRequirements: false;
      isExpanded: undefined;
    };

export const JobApplicationCard = ({
  isMobile,
  userId,
  jobId,
  seekAdvertiserId,
  currentBucket,
  shouldShowNewBadge,
  shouldShowBillableBadge,
  shouldShowVerifiedCredentialsBadge,
  jobApplication,
  lastRole,
  matchedQualities,
  homeLocation,
  salary,
  education,
  certsyVerifiedFullName,
  attachments,
  emailCount,
  previousApplicationCount,
  noteCount,
  checkboxProps,
  locale,
  applicationListState,
  roleRequirements,
  openDetailsDrawer,
  openDetailsDrawerProfileTab,
  markApplicationAsRead,
  changeStatus,
  onClickRating,
  isVerifiedId,
}: JobApplicationCardProps) => {
  const { t } = useTranslations(translations);

  const [roleRequirementsDisclosure, setRoleRequirementsDisclosure] =
    useState<RoleRequirementsDisclosureState>(() =>
      roleRequirements.length > 0
        ? {
            hasRoleRequirements: true,
            isExpanded: applicationListState === 'expanded',
          }
        : {
            hasRoleRequirements: false,
          },
    );

  const toggleIsExpanded = () => {
    if (roleRequirementsDisclosure.hasRoleRequirements) {
      const newState = !roleRequirementsDisclosure.isExpanded;
      setRoleRequirementsDisclosure({
        hasRoleRequirements: true,
        isExpanded: newState,
      });
      trackCandidateCardStatus({
        actionOrigin: 'Candidate Listing - Individual Card Change',
        candidateCardStatus: newState ? 'Expand' : 'Collapse',
      });
    }
  };

  useEffect(() => {
    if (roleRequirementsDisclosure.hasRoleRequirements) {
      setRoleRequirementsDisclosure({
        hasRoleRequirements: true,
        isExpanded: applicationListState === 'expanded',
      });
    }
  }, [applicationListState, roleRequirementsDisclosure.hasRoleRequirements]);

  return (
    <Card isUnread={jobApplication.isUnread}>
      <div className={styles.grid}>
        <Header
          checkboxProps={checkboxProps}
          jobApplication={jobApplication}
          shouldShowNewBadge={shouldShowNewBadge}
          shouldShowBillableBadge={shouldShowBillableBadge}
          shouldShowVerifiedCredentialsBadge={
            shouldShowVerifiedCredentialsBadge
          }
          openDetailsDrawerProfileTab={() =>
            openDetailsDrawerProfileTab(roleRequirementsDisclosure.isExpanded)
          }
          onClickRating={onClickRating}
          currentBucket={currentBucket}
          locale={locale}
          certsyVerifiedFullName={certsyVerifiedFullName}
        />

        {lastRole && (
          <Row
            marginTop="medium"
            right={<LastRole lastRole={lastRole} locale={locale} />}
          />
        )}

        {Boolean(homeLocation || salary || education) && (
          <Row
            marginTop={lastRole ? 'small' : 'medium'}
            right={
              <ProfileDetails
                salary={salary}
                education={education}
                homeLocation={homeLocation}
                locale={locale}
              />
            }
          />
        )}

        {roleRequirementsDisclosure.hasRoleRequirements && (
          <Row
            marginTop="medium"
            right={
              <RoleRequirementsDisclosure
                jobApplicationId={jobApplication.id}
                isMobile={isMobile}
                roleRequirements={roleRequirements}
                isExpanded={roleRequirementsDisclosure.isExpanded}
                toggleIsExpanded={toggleIsExpanded}
              />
            }
          />
        )}

        {matchedQualities.hasError && (
          <Row
            marginTop="medium"
            right={
              <Notice tone="info">
                <Text size="small">
                  {t(
                    'We’re unable to load skills for this candidate, please assess their profile and resume.',
                  )}
                </Text>
              </Notice>
            }
          />
        )}
        {matchedQualities.qualities.length > 0 && (
          <Row
            marginTop={
              roleRequirementsDisclosure.isExpanded
                ? 'medium'
                : ['small', 'xsmall']
            }
            right={
              <MatchedQualities
                userId={userId}
                jobId={jobId}
                candidateFullname={jobApplication.fullName}
                seekAdvertiserId={seekAdvertiserId}
                qualities={matchedQualities.qualities}
              />
            }
          />
        )}

        <ActionsRow
          attachments={attachments}
          currentBucket={currentBucket}
          emailCount={emailCount}
          noteCount={noteCount}
          isMobile={isMobile}
          previousApplicationCount={previousApplicationCount}
          jobApplication={jobApplication}
          changeStatus={async (bucket: BUCKETS) => {
            changeStatus(bucket, roleRequirementsDisclosure.isExpanded);
          }}
          markApplicationAsRead={markApplicationAsRead}
          openDetailsDrawer={(tab: JOB_APPLICATION_TAB) =>
            openDetailsDrawer(tab, roleRequirementsDisclosure.isExpanded)
          }
          isVerifiedId={isVerifiedId}
        />
      </div>
    </Card>
  );
};

const Row: FC<{
  left?: JSX.Element;
  right: JSX.Element | null;
  marginTop?: ComponentProps<typeof Box>['marginTop'];
}> = ({ left, right, marginTop }) => (
  <>
    {left && (
      <Box marginTop={marginTop} className={styles.leftColumn}>
        {left}
      </Box>
    )}
    <Box marginTop={marginTop} className={styles.rightColumn}>
      {right}
    </Box>
  </>
);

const Header: FC<
  { openDetailsDrawerProfileTab: () => void } & Pick<
    JobApplicationCardProps,
    | 'shouldShowNewBadge'
    | 'shouldShowBillableBadge'
    | 'shouldShowVerifiedCredentialsBadge'
    | 'jobApplication'
    | 'checkboxProps'
    | 'locale'
    | 'onClickRating'
    | 'currentBucket'
    | 'certsyVerifiedFullName'
  >
> = ({
  jobApplication,
  shouldShowNewBadge,
  shouldShowBillableBadge,
  shouldShowVerifiedCredentialsBadge,
  checkboxProps,
  openDetailsDrawerProfileTab,
  onClickRating,
  locale,
  currentBucket,
  certsyVerifiedFullName,
}) => {
  const { t } = useTranslations(translations);
  const hasBadges =
    shouldShowNewBadge ||
    shouldShowBillableBadge ||
    shouldShowVerifiedCredentialsBadge;
  const [currentHoverIndex, setCurrentHoverIndex] = useState(0);
  const onStarClick = (ratingValue: number) => {
    const newRatingValue = onClickRating(ratingValue);
    setCurrentHoverIndex(newRatingValue);
  };

  const headerCandidateName = (
    <Text
      size="large"
      weight={jobApplication.isUnread ? 'strong' : 'regular'}
      baseline
    >
      <Box
        data-hj-masked
        tabIndex={0}
        role="button"
        cursor="pointer"
        onKeyDown={buildHandleEnterAndSpaceKeyPress(
          openDetailsDrawerProfileTab,
        )}
        onClick={openDetailsDrawerProfileTab}
        display="flex"
        justifyContent="spaceBetween"
      >
        {jobApplication.fullName}
      </Box>
    </Text>
  );
  const headerAppliedDate = (
    <AppliedDate
      appliedDate={jobApplication.appliedDate}
      source={jobApplication.source}
      locale={locale}
    />
  );

  const starRating = (
    <Box
      paddingRight={
        currentBucket === BUCKETS.INBOX ? ['none', 'xsmall'] : 'none'
      }
    >
      <Inline space="xsmall">
        {[1, 2, 3, 4, 5].map((starNumber) => (
          <Box
            key={`${starNumber}-rating`}
            aria-label={t('Star rating', { index: starNumber })}
            role="button"
            tabIndex={0}
            onMouseLeave={() => setCurrentHoverIndex(0)}
            onMouseEnter={() => setCurrentHoverIndex(starNumber)}
            onClick={() => onStarClick(starNumber)}
            cursor="pointer"
          >
            <Text size="small">
              <div className={styles.starRatingColor}>
                <IconStar
                  titleId={`IconStar-${starNumber}`}
                  title={t('Star rating image')}
                  active={
                    jobApplication.rating >= starNumber ||
                    currentHoverIndex >= starNumber
                  }
                />
              </div>
            </Text>
          </Box>
        ))}
      </Inline>
    </Box>
  );

  const checkbox = (
    <CheckboxStandalone
      size="small"
      id={`checkbox-${jobApplication.id}`}
      aria-label={t('Select candidate fullName', {
        fullName: jobApplication.fullName,
      })}
      checked={checkboxProps.checked}
      onChange={checkboxProps.onChange}
    />
  );

  const candidateNameAndVerifiedId = (
    <Inline space="xsmall" alignY="center">
      {headerCandidateName}
      {certsyVerifiedFullName ? (
        <Box paddingTop="xxsmall">
          <CertsyIdentityVerified
            idSuffix={jobApplication.id}
            certsyVerifiedFullName={certsyVerifiedFullName}
          />
        </Box>
      ) : null}
    </Inline>
  );

  if (hasBadges) {
    return (
      <>
        <Row
          right={
            <Box
              display="flex"
              alignItems="center"
              justifyContent="spaceBetween"
            >
              <Inline space="xxsmall" alignY="center">
                {shouldShowNewBadge && <NewBadge />}
                {shouldShowBillableBadge && <BillableBadge />}
                {shouldShowVerifiedCredentialsBadge && (
                  <VerifiedCredentialsBadge idSuffix={jobApplication.id} />
                )}
              </Inline>
              <Hidden below="tablet">{starRating}</Hidden>
            </Box>
          }
        />
        <Row
          marginTop="xsmall"
          left={checkbox}
          right={candidateNameAndVerifiedId}
        />
        <Row marginTop="xsmall" right={headerAppliedDate} />
        <Hidden above="mobile">
          <Row marginTop="medium" right={starRating} />
        </Hidden>
      </>
    );
  }

  return (
    <>
      <Row
        left={checkbox}
        right={
          <div className={styles.nameAndStarRatingContainerWhenNoBadges}>
            {candidateNameAndVerifiedId}
            <Hidden below="tablet">{starRating}</Hidden>
          </div>
        }
      />
      <Row marginTop="xsmall" right={headerAppliedDate} />
      <Hidden above="mobile">
        <Row marginTop="medium" right={starRating} />
      </Hidden>
    </>
  );
};

const LastRole: FC<{
  lastRole: NonNullable<JobApplicationCardProps['lastRole']>;
  locale: JobApplicationCardProps['locale'];
}> = ({ lastRole, locale }) => {
  const [timeSpan, setTimeSpan] = useState('');

  const { t } = useTranslations(translations);
  useEffect(() => {
    date
      .getTimeSpan(lastRole.from, lastRole.to, locale)
      .then((result) => setTimeSpan(result));
  }, [lastRole.from, lastRole.to, locale]);

  // FIXME: It's not good practice to just render one word to join multiple strings
  // because there could be different word ordering in different languages.
  // However, let's keep it as-is to keep it consistent with the old version
  const atWord =
    Boolean(lastRole.title) && Boolean(lastRole.company) ? t('at') : '';

  return (
    <Text size="small">
      <span style={{ fontWeight: 'bold' }}>
        {lastRole.title} {atWord}{' '}
      </span>
      <span data-hj-masked style={{ fontWeight: 'bold' }}>
        {lastRole.company}
      </span>{' '}
      <span data-hj-masked style={{ whiteSpace: 'nowrap' }}>
        ({timeSpan})
      </span>
    </Text>
  );
};

const ProfileDetails: FC<
  Pick<
    JobApplicationCardProps,
    'salary' | 'education' | 'homeLocation' | 'locale'
  >
> = ({ salary, education, homeLocation, locale }) => {
  const { t } = useTranslations(translations);
  const elements: {
    salary?: string;
    education?: {
      tooltipText: string;
      text: string;
    };
    location?: string;
  } = {};

  if (homeLocation) {
    elements.location = homeLocation.description;
  }

  if (salary) {
    elements.salary = `${formatSalaryValues({
      currency: salary.currency,
      minimumSalaryValue: salary.minimumSalaryValue,
      maximumSalaryValue: salary.maximumSalaryValue,
      locale,
    })}  ${salary.salaryType !== null && t(salary.salaryType)}`;
  }

  if (education) {
    let educationText = education.name;
    if (education.completionYear) {
      educationText += `, ${education.completionYear}`;
    }
    elements.education = {
      tooltipText: education.institute,
      text: educationText,
    };
  }

  const desktopElements: Array<{
    tooltipText?: string;
    text: string;
    icon: ReactElement;
  }> = [];
  if (elements.location) {
    desktopElements.push({
      text: elements.location,
      icon: <IconLocation />,
    });
  }

  if (elements.salary) {
    desktopElements.push({
      text: elements.salary,
      icon: <IconMoney />,
    });
  }
  if (elements.education) {
    desktopElements.push({
      tooltipText: elements.education.tooltipText,
      text: elements.education.text,
      icon: <IconEducation />,
    });
  }

  const renderMobileRow = ({
    icon,
    text,
  }: {
    icon: ReactElement;
    text: string;
  }) => (
    <Columns space="xxsmall" alignY="top">
      <Column width="content">{icon}</Column>
      <Column>
        <Box style={{ paddingTop: '6px' }}>
          <Text size="small">{text}</Text>
        </Box>
      </Column>
    </Columns>
  );

  return (
    <>
      <Hidden above="mobile">
        <Stack space="xxsmall">
          {elements.location
            ? renderMobileRow({
                icon: <IconLocation size="xsmall" />,
                text: elements.location,
              })
            : null}

          {elements.salary
            ? renderMobileRow({
                icon: <IconMoney size="xsmall" />,
                text: elements.salary,
              })
            : null}

          {elements.education
            ? renderMobileRow({
                icon: <IconEducation size="xsmall" />,
                text: elements.education.text,
              })
            : null}
        </Stack>
      </Hidden>

      <Hidden below="tablet">
        <Inline space="small" alignY="center" collapseBelow="tablet">
          {desktopElements.map((element, index, array) => {
            const jsxElement = element.tooltipText ? (
              <TooltipRenderer
                key={index}
                id="tooltip-profile-details"
                tooltip={<Text>{element.tooltipText}</Text>}
              >
                {({ triggerProps }) => (
                  <Box {...triggerProps}>
                    <Text size="small" key={index} icon={element.icon}>
                      {element.text}
                    </Text>
                  </Box>
                )}
              </TooltipRenderer>
            ) : (
              <Text key={index} size="small" icon={element.icon}>
                {element.text}
              </Text>
            );

            if (index + 1 === array.length) {
              return jsxElement;
            }
            return <Box key={index}>{jsxElement}</Box>;
          })}
        </Inline>
      </Hidden>
    </>
  );
};

const ActionsRow: FC<
  Pick<
    JobApplicationCardProps,
    | 'attachments'
    | 'isMobile'
    | 'jobApplication'
    | 'previousApplicationCount'
    | 'openDetailsDrawer'
    | 'emailCount'
    | 'noteCount'
    | 'currentBucket'
    | 'markApplicationAsRead'
    | 'changeStatus'
    | 'isVerifiedId'
  >
> = ({
  attachments,
  isMobile,
  jobApplication,
  previousApplicationCount,
  openDetailsDrawer,
  markApplicationAsRead,
  changeStatus,
  emailCount,
  noteCount,
  currentBucket,
  isVerifiedId,
}) => {
  const { t } = useTranslations(translations);

  const attachmentButtonIcons = attachments.length > 0 && (
    <AttachmentButtons
      attachments={attachments}
      isMobile={isMobile}
      jobApplication={jobApplication}
      markApplicationAsRead={markApplicationAsRead}
      openDetailsDrawer={openDetailsDrawer}
      isVerifiedId={isVerifiedId}
    />
  );

  const previousApplicationButtonIcon = previousApplicationCount > 0 && (
    <Inline space="xsmall" alignY="center">
      <ButtonIcon
        variant="transparent"
        id={`${jobApplication.id}-previous-applications`}
        icon={<IconHistory />}
        label={t('Previous Applications')}
        onClick={() => {
          openDetailsDrawer(JOB_APPLICATION_TAB.PREVIOUS_APPLICATIONS);
          trackClickedPreviousApplicationsInCandidateList({
            candidateId: jobApplication.candidateId,
            currentPage: TRACKING_CANDIDATE_LIST,
            isVerifiedId,
          });
        }}
      />
      <Text size="small">{previousApplicationCount}</Text>
    </Inline>
  );

  const emailButtonIcon = emailCount > 0 && (
    <Box data-cy="application-card-email">
      <Inline space="xsmall" alignY="center">
        <ButtonIcon
          variant="transparent"
          id={`${jobApplication.id}-emails`}
          icon={<IconMail />}
          label={t('Emails')}
          onClick={() => {
            openDetailsDrawer(JOB_APPLICATION_TAB.EMAILS);
            trackClickedEmailsInCandidateList({
              candidateId: jobApplication.candidateId,
              currentPage: TRACKING_CANDIDATE_LIST,
              isVerifiedId,
            });
          }}
        />
        <Text size="small">{emailCount}</Text>
      </Inline>
    </Box>
  );

  const noteButtonIcon = noteCount > 0 && (
    <Inline space="xsmall" alignY="center">
      <ButtonIcon
        variant="transparent"
        id={`${jobApplication.id}-notes`}
        icon={<IconCompose />}
        label={t('Notes & Attachments')}
        onClick={() => {
          openDetailsDrawer(JOB_APPLICATION_TAB.NOTES_ATTACHMENTS);
          trackClickedNotesInCandidateList({
            candidateId: jobApplication.candidateId,
            currentPage: TRACKING_CANDIDATE_LIST,
            isVerifiedId,
          });
        }}
      />
      <Text size="small">{noteCount}</Text>
    </Inline>
  );

  const changeStatusButtons =
    currentBucket === BUCKETS.INBOX ? (
      <>
        <Box display="flex" alignItems="center" justifyContent="spaceBetween">
          <Hidden above="mobile">
            <MoveStatusMenu
              jobApplication={jobApplication}
              changeStatus={changeStatus}
              currentBucket={currentBucket}
            />
          </Hidden>
          <Inline space={['none', 'small']} alignY="center">
            <NotSuitableAndShortlistButtons
              fullName={jobApplication.fullName}
              changeStatus={changeStatus}
            />
            <Hidden below="tablet">
              <MoveStatusMenu
                jobApplication={jobApplication}
                changeStatus={changeStatus}
                currentBucket={currentBucket}
              />
            </Hidden>
          </Inline>
        </Box>
      </>
    ) : (
      <MoveStatusMenu
        jobApplication={jobApplication}
        changeStatus={changeStatus}
        currentBucket={currentBucket}
      />
    );

  if (isMobile) {
    return (
      <>
        {Boolean(
          attachmentButtonIcons ||
            previousApplicationButtonIcon ||
            emailButtonIcon ||
            noteButtonIcon,
        ) && (
          <Row
            marginTop="medium"
            right={
              <Inline space="medium">
                {attachmentButtonIcons}
                {previousApplicationButtonIcon}
                {emailButtonIcon}
                {noteButtonIcon}
              </Inline>
            }
          />
        )}
        <Row marginTop="medium" right={changeStatusButtons} />
      </>
    );
  }

  return (
    <Row
      marginTop="medium"
      right={
        <Box display="flex" alignItems="center" justifyContent="spaceBetween">
          <Inline space="medium">
            {attachmentButtonIcons}
            {previousApplicationButtonIcon}
            {emailButtonIcon}
            {noteButtonIcon}
          </Inline>

          {changeStatusButtons}
        </Box>
      }
    />
  );
};

/**
 * Card is a custom component extracted from Braid's Card:
 * https://github.com/seek-oss/braid-design-system/blob/c2e55000b0665ea0d3cb66edbdb42bb946599280/packages/braid-design-system/src/lib/components/Card/Card.tsx
 *
 * We need this version because we want the Card to have border _before_ NVL version
 * because we would need the border and custom hover style for the card to stand out on a white background (to be implemented soon)
 */
const Card: FC<PropsWithChildren<{ isUnread: boolean }>> = ({
  isUnread,
  children,
}) => (
  <Box
    background="surface"
    padding="medium"
    borderRadius="standard"
    position="relative"
    marginX={['xsmall', 'none']}
    className={{
      [styles.applicationCard]: true,
    }}
    data={{ cy: 'job-application-card' }}
  >
    {isUnread && (
      <Box
        component="span"
        position="absolute"
        top={0}
        bottom={0}
        left={0}
        overflow="hidden"
        borderRadius="standard"
      >
        <Box
          component="span"
          height="full"
          display="inlineBlock"
          style={{
            width: vars.grid,
            background: vars.borderColor.formAccent,
          }}
        />
      </Box>
    )}
    {children}
  </Box>
);
